//miscellaneous functions

/**
 * Replaces the query string of the current URL with the specified query string.
 * @param {string} queryString - The new query string.
 */
function replaceQueryString(queryString) {
    window.history.replaceState(null, null, queryString);
}

/**
 * Removes all classes from the specified element that have the specified prefix.
 * @param {HTMLElement} element - The element to remove classes from.
 * @param {string} prefix - The prefix of the classes to remove.
 * @returns {HTMLElement} - The modified element.
 */
function removeClassByPrefix(element, prefix) {
    var regx = new RegExp('\\b' + prefix + '[^ ]*[ ]?\\b', 'g');
    element.className = element.className.replace(regx, '');
    return element;
}

/**
 * Adds an event listener to the current element and its clones.
 * @param {string} type - The type of the event.
 * @param {Function} func - The event handler function.
 * @param {boolean} capture - Specifies whether the event should be captured during the event propagation.
 */
HTMLElement.prototype.addEventListenerEx = function (type, func, capture) {
    var target = this;
    var newTarget = target.cloneNode(true);
    target.parentNode.replaceChild(newTarget, target);
    newTarget.addEventListener(type, func, capture);
}

/**
 * Adds a click event listener to the current element and its clones.
 * @param {Function} func - The click event handler function.
 * @param {boolean} capture - Specifies whether the event should be captured during the event propagation.
 */
HTMLElement.prototype.addClick = function (func, capture) {
    this.addEventListenerEx('click', func, capture);
}

/**
 * Adds an event listener to the element with the specified id and its clones.
 * @param {string} id - The id of the element.
 * @param {string} type - The type of the event.
 * @param {Function} func - The event handler function.
 * @param {boolean} capture - Specifies whether the event should be captured during the event propagation.
 */
function addEventListenerById(id, type, func, capture) {
    document.getElementById(id).addEventListenerEx(type, func, capture);
}

/**
 * Adds an event listener to all elements with the specified class name and their clones.
 * @param {string} className - The class name of the elements.
 * @param {string} type - The type of the event.
 * @param {Function} func - The event handler function.
 * @param {boolean} capture - Specifies whether the event should be captured during the event propagation.
 */
function addEventListenerByClass(className, type, func, capture) {
    const targets = document.querySelectorAll(className);
    targets.forEach(target => target.addEventListenerEx(type, func, capture));
}

/**
 * Adds a click event listener to the element with the specified id and its clones.
 * @param {string} id - The id of the element.
 * @param {Function} func - The click event handler function.
 * @param {boolean} capture - Specifies whether the event should be captured during the event propagation.
 */
function addClickById(id, func, capture) {
    addEventListenerById(id, 'click', func, capture);
}

/**
 * Adds a click event listener to all elements with the specified class name and their clones.
 * @param {string} className - The class name of the elements.
 * @param {Function} func - The click event handler function.
 * @param {boolean} capture - Specifies whether the event should be captured during the event propagation.
 */
function addClickByClass(className, func, capture) {
    addEventListenerByClass(className, 'click', func, capture);
}

/**
 * Generates a version 4 UUID (Universally Unique Identifier) using the random number generator.
 * @returns {string} - The generated UUID.
 */
function createGuidV4() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
        .replace(/[xy]/g, function (c) {
            const r = Math.random() * 16 | 0,
                v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
}

/**
 * Retrieves the computed value of the CSS `display` property for the specified element.
 * @param {HTMLElement} element - The element to check.
 * @returns {string} - The computed value of the `display` property.
 */
function elementDisplayAttr(element) {
    return window.getComputedStyle(element, null).display;
}

/**
 * Checks if the specified element is hidden.
 * @param {HTMLElement} element - The element to check.
 * @returns {boolean} - True if the element is hidden, false otherwise.
 */
function elementIsHidden(element) {
    return elementDisplayAttr(element) == "none";
}

/**
 * Checks if the specified variable is not null.
 * @param {*} variable - The variable to check.
 * @returns {boolean} - True if the variable is not null, false otherwise.
 */
const isNotNull = (variable) => {
    if (variable === null) {
        return false;
    }
    return true;
};

/**
 * Checks if an element with the specified class name exists in the document.
 * @param {string} className - The class name to check.
 * @returns {boolean} - True if an element with the class name exists, false otherwise.
 */
const doesClassExist = (className) => {
    const element = document.querySelector(`.${className}`);
    return element !== null;
};

/**
 * Checks if the current browser matches the specified browser type.
 * @param {string} browserType - The browser type to check.
 * @returns {boolean} - True if the current browser matches the specified browser type, false otherwise.
 */
const isBrowser = (browserType) => {
    const userAgent = navigator.userAgent.toLowerCase();

    switch (browserType.toLowerCase()) {
        case 'chrome':
            return userAgent.includes('chrome') && !userAgent.includes('edge') && !userAgent.includes('opr');
        case 'firefox':
            return userAgent.includes('firefox');
        case 'safari':
            return userAgent.includes('safari') && !userAgent.includes('chrome');
        case 'edge':
            return userAgent.includes('edg') || userAgent.includes('edge');
        case 'opera':
            return userAgent.includes('opr') || userAgent.includes('opera');
        case 'ie':
            return userAgent.includes('msie') || userAgent.includes('trident');
        case 'brave':
            if (typeof navigator.brave === 'undefined') {
                return userAgent.includes('brave');
            }
            return navigator.brave.isBrave();
        case 'vivaldi':
            return userAgent.includes('vivaldi');
        case 'yandex':
            return userAgent.includes('yabrowser');
        case 'uc':
            return userAgent.includes('ucbrowser');
        case 'maxthon':
            return userAgent.includes('maxthon');
        case 'qq':
            return userAgent.includes('qqbrowser');
        default:
            return false;
    }
};

/**
 * Strips spaces from the input element value.
 * @param {HTMLElement} inputElement - The input element.
 */
const stripSpacesFromInput = (inputElement) => {
    inputElement.addEventListener('input', (event) => {
        const inputValue = event.target.value;
        const strippedValue = inputValue.replace(/\s+/g, '');
        event.target.value = strippedValue;
    });
};

/**
 * Copies the specified text to the clipboard.
 * @param {string} text - The text to copy.
 * @param {string} buttonId - The ID of the button. If specified, a checkmark icon will be added to the button after the text is copied.
 */
const copyToClipboard = (text, buttonId) => {
    navigator.clipboard.writeText(text).then(() => {
        debug('Text copied to clipboard: ' + text);
        if (buttonId) {
            const button = document.getElementById(buttonId);
            if (!button.querySelector('.icon-check')) {
                button.innerHTML += '<span class="icon-button icon-default icon-check" aria-hidden="true" data-font-icon></span>';
            }
        }
    }).catch(err => {
        error('Failed to copy text: ' + err);
    });
};